import { useState, useEffect, useRef } from "react";
import { address as toAddress } from "@solana/kit";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  Loader2,
  Clipboard,
  ExternalLink,
} from "lucide-react";
import { useTranslation } from "@/i18n";
import { useSolanaSendTransaction } from "@/hooks/useSolanaSendTransaction";
import { getExplorerTransactionUrl, shortenAddress } from "@/lib/solana/format";
import { useBrowserWalletStore } from "../index";
import { transactionRelay } from "@/services/transaction-relay";
import type { TransactionResult } from "@/types/solana";
import { AuthorityCard, VNextScope } from "@/components/brand/vnext";

type TokenId = "SOL" | "USDC" | "EVER";
type Step = "form" | "confirming" | "success" | "error" | "unknown" | "recovering";

type TokenColor = "emerald" | "blue" | "purple";

interface TokenInfo {
  id: TokenId;
  symbol: string;
  balance: number;
  decimals: number;
  mint?: string;
  color: TokenColor;
}

export interface TransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultToken?: TokenId;
  walletAddress: string;
  solBalance: number | null;
  paymentBalance: number;
  paymentDecimals: number;
  paymentMint?: string;
  governanceBalance: number;
  governanceDecimals: number;
  governanceMint?: string;
  onTransferComplete?: () => void;
}

export function TransferDialog({
  open,
  onOpenChange,
  defaultToken = "SOL",
  walletAddress,
  solBalance,
  paymentBalance,
  paymentDecimals,
  paymentMint,
  governanceBalance,
  governanceDecimals,
  governanceMint,
  onTransferComplete,
}: TransferDialogProps) {
  const { t } = useTranslation();
  const { sendSOL, sendToken, recover, isLoading } = useSolanaSendTransaction();
  const network = useBrowserWalletStore((state) => state.network);

  const [selectedToken, setSelectedToken] = useState<TokenId>(defaultToken);
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState<Step>("recovering");
  const [operationId, setOperationId] = useState("");
  const [displayWallet, setDisplayWallet] = useState(walletAddress);
  const visibleStep = displayWallet === walletAddress ? step : "recovering";
  const submitting = useRef(false);
  const recovering = useRef(false);
  const generation = useRef(0);
  const [txSignature, setTxSignature] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    generation.current += 1;
    async function restore() {
      try {
        const result = await recover();
        if (cancelled) return;
        setDisplayWallet(walletAddress);
        setTxSignature(result?.signature ?? "");
        setOperationId(result?.operationId ?? "");
        setStep(!result ? "form" : result.confirmed ? "success" : result.status === "failed" ? "error" : "unknown");
        setErrorMessage(result?.status === "failed" ? t("wallet.transferFailedOnChain") : "");
      } catch {
        if (!cancelled) {
          setDisplayWallet(walletAddress);
          setTxSignature("");
          setOperationId("");
          setStep("unknown");
          setErrorMessage(t("wallet.transferRecoveryUnavailable"));
        }
      }
    }
    void restore();
    return () => { cancelled = true; generation.current += 1; };
  }, [open, walletAddress, recover, t]);

  const applyResult = (result: TransactionResult) => {
    setTxSignature(result.signature);
    setOperationId(result.operationId ?? "");
    if (result.confirmed) {
      setStep("success");
      setErrorMessage("");
      try { onTransferComplete?.(); } catch { setErrorMessage(t("wallet.transferBalanceRefreshUnavailable")); }
    } else if (result.status === "unknown" || result.status === "submitted") {
      setStep("unknown");
      setErrorMessage("");
    } else {
      setErrorMessage(result.status === "failed" ? t("wallet.transferFailedOnChain") : result.message || t("wallet.transferNotSubmitted"));
      setStep("error");
    }
  };

  const handleRecover = async () => {
    if (recovering.current) return;
    recovering.current = true;
    const requestGeneration = generation.current;
    setStep("recovering");
    try {
      const result = await recover();
      if (requestGeneration !== generation.current) return;
      if (result) applyResult(result);
      else setStep("form");
    } catch {
      if (requestGeneration !== generation.current) return;
      setStep("unknown");
      setErrorMessage(t("wallet.transferRecoveryUnavailable"));
    } finally {
      recovering.current = false;
    }
  };

  const tokens: TokenInfo[] = [
    {
      id: "SOL",
      symbol: "SOL",
      balance: solBalance ?? 0,
      decimals: 9,
      color: "emerald",
    },
    {
      id: "USDC",
      symbol: "USDC",
      balance: paymentBalance,
      decimals: paymentDecimals,
      mint: paymentMint,
      color: "blue",
    },
    {
      id: "EVER",
      symbol: "EVER",
      balance: governanceBalance,
      decimals: governanceDecimals,
      mint: governanceMint,
      color: "purple",
    },
  ];

  // Always guaranteed since selectedToken matches one of the three token ids
  const currentToken = tokens.find(
    (tk) => tk.id === selectedToken,
  ) as TokenInfo;

  const getMaxAmount = (): number => {
    if (selectedToken === "SOL") {
      return Math.max(0, (solBalance ?? 0) - 0.01);
    }
    return currentToken.balance;
  };

  const validateAddress = (addr: string): string | null => {
    if (!addr.trim()) return null;
    // Kit's `address()` throws on invalid base58 / non-32-byte — same shape
    // check as web3.js's `new PublicKey()` did previously.
    try {
      toAddress(addr);
    } catch {
      return t("wallet.invalidAddress");
    }
    if (addr === walletAddress) {
      return t("wallet.selfTransferError");
    }
    return null;
  };

  const validateAmount = (val: string): string | null => {
    const num = parseFloat(val);
    if (!val || isNaN(num) || num <= 0) return null;
    if (num > getMaxAmount()) {
      return t("wallet.insufficientBalance");
    }
    return null;
  };

  const isFormValid = (): boolean => {
    const num = parseFloat(amount);
    if (!recipient.trim() || !amount || isNaN(num) || num <= 0) return false;
    if (validateAddress(recipient)) return false;
    if (num > getMaxAmount()) return false;
    return true;
  };

  // A plain handler like its siblings: its inputs (form state, the derived
  // token, the validity check) change with every keystroke, so a memo here
  // never held and only mis-stated its dependencies.
  const handleSend = async () => {
    if (!isFormValid() || submitting.current) return;
    submitting.current = true;
    const requestGeneration = generation.current;

    setStep("confirming");
    setErrorMessage("");

    try {
      const parsedAmount = amount;
      let result;

      if (selectedToken === "SOL") {
        result = await sendSOL(recipient, parsedAmount);
      } else {
        const mint = currentToken.mint;
        if (!mint) throw new Error("Token mint not available");
        result = await sendToken(
          recipient,
          mint,
          parsedAmount,
          currentToken.decimals,
          currentToken.symbol,
        );
      }

      if (requestGeneration === generation.current) applyResult(result);
    } catch (err: unknown) {
      if (requestGeneration !== generation.current) return;
      const msg = err instanceof Error ? err.message : "Unknown error";
      setErrorMessage(msg);
      setStep("error");
    } finally {
      submitting.current = false;
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setRecipient(text.trim());
    } catch {
      // Clipboard access denied
    }
  };

  const handleMax = () => {
    const max = getMaxAmount();
    if (max > 0) {
      setAmount(
        selectedToken === "SOL"
          ? max.toFixed(4)
          : max.toFixed(currentToken.decimals <= 6 ? currentToken.decimals : 4),
      );
    }
  };

  const handleReset = () => {
    setStep("form");
    setRecipient("");
    setAmount("");
    setTxSignature("");
    setOperationId("");
    setErrorMessage("");
    setSelectedToken(defaultToken);
  };

  const handleClose = (openState: boolean) => {
    if (!openState) {
      if (submitting.current || isLoading) return;
      setStep("recovering");
    }
    onOpenChange(openState);
  };

  const addressError = recipient ? validateAddress(recipient) : null;
  const amountError = amount ? validateAmount(amount) : null;

  const tokenColors: Record<TokenColor, { selected: string; dot: string }> = {
    emerald: {
      selected: "bg-brand-blue-100 border-brand-blue-300 text-brand-blue-600",
      dot: "bg-brand-blue-600",
    },
    blue: {
      selected: "bg-blue-100 border-blue-300 text-blue-600",
      dot: "bg-blue-600",
    },
    purple: {
      selected: "bg-purple-100 border-purple-300 text-purple-600",
      dot: "bg-purple-600",
    },
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[420px] bg-background border-border text-foreground p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-purple-500/15">
              <ArrowUpRight className="h-4 w-4 text-purple-400" />
            </div>
            {t("wallet.transfer")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("wallet.transfer")}
          </DialogDescription>
        </DialogHeader>

        <VNextScope><AuthorityCard
          authority={{ tier: "T3", state: txSignature ? "authorized" : "approval-required", label: t("wallet.transferAuthority") }}
          executionStatus={visibleStep === "form" ? "approval-required" : visibleStep === "success" ? "settled" : visibleStep === "unknown" || visibleStep === "recovering" ? "unknown" : visibleStep === "error" ? "failed" : "submitting"}
        >
        {/* Step: Form */}
        {visibleStep === "form" && (
          <div className="px-6 pb-6 space-y-5">
            {/* Token selector */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t("wallet.selectToken")}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {tokens.map((token) => {
                  const colors = tokenColors[token.color];
                  const isSelected = selectedToken === token.id;
                  return (
                    <button
                      key={token.id}
                      onClick={() => setSelectedToken(token.id)}
                      className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl border transition-all ${
                        isSelected
                          ? colors.selected
                          : "bg-secondary border-border text-muted-foreground hover:bg-secondary hover:border-border"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <div className={`h-2 w-2 rounded-full ${colors.dot}`} />
                        <span className="text-sm font-medium">
                          {token.symbol}
                        </span>
                      </div>
                      <span className="text-[11px] text-neutral-500 font-mono tabular-nums">
                        {token.balance.toFixed(
                          token.decimals <= 6 ? Math.min(token.decimals, 4) : 4,
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Recipient */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t("wallet.recipientAddress")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  aria-label={t("wallet.recipientAddress")}
                  placeholder="So1ana..."
                  className={`w-full bg-secondary border rounded-xl px-4 py-3 pr-16 text-sm font-mono text-foreground/80 placeholder:text-neutral-600 outline-none transition-colors ${
                    addressError
                      ? "border-red-500/40 focus:border-red-500/60"
                      : "border-border focus:border-purple-500/30"
                  }`}
                />
                <button
                  type="button"
                  onClick={handlePaste}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground/80 hover:bg-secondary transition-colors"
                >
                  <Clipboard className="h-3 w-3" />
                  {t("wallet.pasteAddress")}
                </button>
              </div>
              {addressError && (
                <p className="text-xs text-red-600">{addressError}</p>
              )}
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("wallet.amount")}
                </label>
                <span className="text-xs text-neutral-500">
                  {t("wallet.available")}:{" "}
                  <span className="font-mono tabular-nums text-muted-foreground">
                    {getMaxAmount().toFixed(4)}
                  </span>{" "}
                  {currentToken.symbol}
                </span>
              </div>
              <div className="relative">
                <input
                  aria-label={t("wallet.amount")}
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="any"
                  className={`w-full bg-secondary border rounded-xl px-4 py-3 pr-20 text-sm font-mono text-foreground/80 placeholder:text-neutral-600 outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    amountError
                      ? "border-red-500/40 focus:border-red-500/60"
                      : "border-border focus:border-purple-500/30"
                  }`}
                />
                <button
                  type="button"
                  onClick={handleMax}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg text-xs font-semibold text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 transition-colors"
                >
                  {t("wallet.maxAmount")}
                </button>
              </div>
              {amountError && (
                <p className="text-xs text-red-600">{amountError}</p>
              )}
              {selectedToken === "SOL" && !amountError && (
                <p className="text-xs text-neutral-500">
                  {t("wallet.solReserveHint")}
                </p>
              )}
            </div>

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={!isFormValid() || isLoading}
              className="w-full py-3 rounded-xl text-sm font-semibold transition-all bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-foreground shadow-lg shadow-purple-500/20 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              {t("wallet.send")}{" "}
              {amount && parseFloat(amount) > 0 ? `${amount} ` : ""}
              {currentToken.symbol}
            </button>
          </div>
        )}

        {/* Step: Confirming */}
        {(visibleStep === "confirming" || visibleStep === "recovering") && (
          <div className="px-6 pb-8 pt-4 flex flex-col items-center gap-4">
            <div className="p-4 rounded-full bg-purple-500/10">
              <Loader2 className="h-8 w-8 text-purple-400 animate-spin" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              {t(visibleStep === "recovering" ? "wallet.checkingOriginalTransaction" : "wallet.confirmInWallet")}
            </p>
          </div>
        )}

        {/* Step: Success */}
        {visibleStep === "success" && (
          <div className="px-6 pb-6 pt-4 flex flex-col items-center gap-4">
            <div className="p-4 rounded-full bg-success/10 animate-in zoom-in-50 duration-300">
              <CheckCircle2 className="h-8 w-8 text-success" />
            </div>
            <p className="text-sm font-medium text-foreground/80">
              {t("wallet.transferSuccess")}
            </p>
            {errorMessage && <p className="text-sm text-muted-foreground">{errorMessage}</p>}
            {txSignature && (
              <button
                onClick={() => {
                  const url = getExplorerTransactionUrl(txSignature, network);
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
                className="flex items-center gap-2 text-xs text-purple-400 hover:text-purple-300 font-mono transition-colors"
              >
                {shortenAddress(txSignature, 8)}
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
            <button
              onClick={() => {
                void transactionRelay.acknowledge(walletAddress, operationId).then(() => handleClose(false)).catch(() => {
                  setStep("unknown");
                  setErrorMessage(t("wallet.transferRecoveryUnavailable"));
                });
              }}
              className="w-full py-2.5 rounded-xl text-sm font-medium bg-secondary hover:bg-secondary text-muted-foreground transition-colors"
            >
              {t("wallet.done")}
            </button>
          </div>
        )}

        {visibleStep === "unknown" && (
          <div className="px-6 pb-6 pt-4 flex flex-col items-center gap-4">
            <p className="text-sm text-muted-foreground text-center" role="status">
              {errorMessage || t("wallet.transferUnknown")}
            </p>
            {txSignature && <a href={getExplorerTransactionUrl(txSignature, network)} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-blue-600 font-mono break-all">{txSignature}</a>}
            <button onClick={() => void handleRecover()} className="w-full py-2.5 rounded-xl text-sm font-medium bg-secondary text-muted-foreground">
              {t("wallet.checkOriginalTransaction")}
            </button>
          </div>
        )}

        {/* Step: Error */}
        {visibleStep === "error" && (
          <div className="px-6 pb-6 pt-4 flex flex-col items-center gap-4">
            <div className="p-4 rounded-full bg-red-500/10 animate-in zoom-in-50 duration-300">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <p className="text-sm text-red-600 text-center max-w-[280px]">
              {errorMessage}
            </p>
            {txSignature && <a href={getExplorerTransactionUrl(txSignature, network)} target="_blank" rel="noopener noreferrer" className="text-xs font-mono break-all">{txSignature}</a>}
            <button
              onClick={() => {
                void transactionRelay.acknowledge(walletAddress, operationId).then(handleReset).catch(() => {
                  setStep("unknown");
                  setErrorMessage(t("wallet.transferRecoveryUnavailable"));
                });
              }}
              className="w-full py-2.5 rounded-xl text-sm font-medium bg-secondary hover:bg-secondary text-muted-foreground transition-colors"
            >
              {t("wallet.tryAgain")}
            </button>
          </div>
        )}
        </AuthorityCard></VNextScope>
      </DialogContent>
    </Dialog>
  );
}
